/**
 * One discovered-candidate row in the local-databases discovery dialog
 * (Workstream B3 + C2/C3): engine icon, container/instance name, a
 * `image · host[:port] · db/user` summary (local candidates have no port), a
 * provenance badge ("Docker" vs "Local"), a credentials badge (green
 * "credentials in container env" vs amber "password asked on connect" —
 * suppressed entirely for `Windows authentication` candidates, since no
 * password is ever asked for those), and a per-row Add button.
 *
 * Add saves the connection — nothing auto-connects. A candidate that already
 * matches a saved connection shows an "already added" badge and a disabled Add
 * whose tooltip names the matching connection (house rule: every disabled
 * button explains why). Add / Added are named after the candidate ("Add
 * orders-db"): a column of buttons all called "Add" does not say which is which.
 */
import { Check } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { ConnectionEngineIcon } from '../components/ConnectionEngineIcon';
import type { Connection } from '../../../types/electron';
import type { DockerCandidate } from '../../../types/dockerDiscovery';
import { safeConnectionName } from '../../../types/safeConnectionDisplay';

function summaryLine(candidate: DockerCandidate): string {
  // Local (native) candidates have no port — SQL Browser resolves it — so the
  // server string stands alone; a docker container shows host:port.
  const endpoint = candidate.kind === 'local' ? candidate.host : `${candidate.host}:${candidate.port}`;
  const parts: string[] = [candidate.imageLabel, endpoint];
  if (candidate.oracleServiceName) parts.push(`service "${candidate.oracleServiceName}"`);
  // Azurite: the account is the connection's server, and the single port above
  // is only the first of up to three published service ports.
  if (candidate.azureStorage) parts.push(`account "${candidate.azureStorage.account}"`);
  if (candidate.database) parts.push(`db "${candidate.database}"`);
  if (candidate.user) parts.push(`user "${candidate.user}"`);
  return parts.join(' · ');
}

interface DockerDiscoveryRowProps {
  candidate: DockerCandidate;
  duplicateConnection?: Connection;
  added: boolean;
  adding: boolean;
  onAdd: () => void;
}

export function DockerDiscoveryRow({
  candidate,
  duplicateConnection,
  added,
  adding,
  onAdd,
}: DockerDiscoveryRowProps) {
  const isDuplicate = Boolean(duplicateConnection) && !added;
  const duplicateName = safeConnectionName(duplicateConnection, 'a saved connection');
  const addName = `Add ${candidate.containerName}`;

  return (
    <div
      className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/50"
      data-testid={`docker-discovery-row-${candidate.key}`}
    >
      <ConnectionEngineIcon engine={candidate.engine} size="sm" />

      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{candidate.containerName}</div>
        <div className="truncate text-xs text-muted-foreground">{summaryLine(candidate)}</div>
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        <Badge
          variant="outline"
          className="text-[10px]"
          data-testid={`docker-discovery-badge-kind-${candidate.key}`}
        >
          {candidate.kind === 'local' ? 'Local' : 'Docker'}
        </Badge>

        {candidate.hasPassword ? (
          <Badge
            variant="outline"
            className="border-emerald-500/40 text-[10px] text-emerald-600 dark:text-emerald-400"
            data-testid={`docker-discovery-badge-env-${candidate.key}`}
          >
            credentials in container env
          </Badge>
        ) : candidate.authentication === 'Windows authentication' ? null : (
          <Badge
            variant="outline"
            className="border-amber-500/40 text-[10px] text-warning"
            data-testid={`docker-discovery-badge-ask-${candidate.key}`}
          >
            password asked on connect
          </Badge>
        )}

        {isDuplicate && (
          <Badge
            variant="outline"
            className="text-[10px]"
            data-testid={`docker-discovery-duplicate-${candidate.key}`}
          >
            already added
          </Badge>
        )}
      </div>

      {added ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0}>
              <Button
                size="sm"
                variant="ghost"
                disabled
                className="gap-1 text-emerald-600 dark:text-emerald-400"
                aria-label={`Added ${candidate.containerName}`}
                data-testid={`docker-discovery-added-${candidate.key}`}
              >
                <Check className="h-4 w-4" />
                Added
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>Saved to your connections — connect when you are ready.</TooltipContent>
        </Tooltip>
      ) : isDuplicate ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0}>
              <Button size="sm" disabled aria-label={addName} data-testid={`docker-discovery-add-${candidate.key}`}>
                Add
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>Already saved as “{duplicateName}”.</TooltipContent>
        </Tooltip>
      ) : (
        <Button
          size="sm"
          onClick={onAdd}
          disabled={adding}
          aria-label={addName}
          data-testid={`docker-discovery-add-${candidate.key}`}
        >
          Add
        </Button>
      )}
    </div>
  );
}
